import { ButcherHeader } from '@components/ButcherHeader';
import { OrderNotesModal } from '@components/Modal/OrderProcessing/OrderNotesModal';
import ServiceNotesModal from '@components/Modal/ServiceNotesModal';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { ServiceTaskProgress } from '@components/OrderProcessing/ServiceTaskProgress';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { useToast } from '@contexts/Toast';
import {
  faCircleCheck,
  faFilePen,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  errorMessage,
  Length,
  OrderProcessingMasterData,
  OrderProcessStatus,
  responseTexts,
  ServiceProcessingCrumbSteps,
} from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { ServiceOrderDetails, ServiceTaskDetails } from '@helpers/Models';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const ServiceProcessServiceDetails: React.FC = () => {
  const { showToastMessage } = useToast();
  const { state } = useLocation();
  // Getting species id, service id and service name from the previous page
  const orderSpeciesId = state?.orderSpeciesId;
  const currentServiceId = state?.currentServiceId;
  const currentServiceName = state?.currentServiceName; // For showing in the breadcrumbs
  const [speciesName, setSpeciesName] = useState();

  const [orderNumber, setOrderNumber] = useState<string>('');
  const [orderDetails, setOrderDetails] = useState<ServiceOrderDetails>();
  const [finalMeasurements, setFinalMeasurements] = useState<{
    [serviceId: number]: number;
  }>({});

  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const [showServiceNotesModal, setShowServiceNotesModal] = useState(false);
  const [serviceNotesModalMode, setServiceNotesModalMode] = useState<
    'view' | 'edit'
  >('view');
  // Add missing state for activeServiceForNotes
  const [activeServiceForNotes, setActiveServiceForNotes] =
    useState<ServiceTaskDetails | null>(null);
  const [currentStep, setCurrentStep] = useState<'inService' | 'completed'>(
    'inService',
  );
  // For tracking the edit mode
  const [editModeId, setEditModeId] = useState<number | null>(null);
  const [editingServiceId, setEditingServiceId] = useState<number | null>(null);

  useEffect(() => {
    fetchServiceTaskDetails();
  }, [orderSpeciesId]);

  /**
   * Effect hook that runs whenever the `services` array in `orderDetails` changes.
   *
   * If services are present, it checks whether all services in the order are completed.
   *
   * Dependencies:
   * - `orderDetails?.services` — Ensures the effect runs only when the list of services changes.
   *
   */
  useEffect(() => {
    if (orderDetails?.services) {
      checkAllServicesCompleted();
    }
  }, [orderDetails?.services]);

  /**
   * Fetches detailed information about a specific order using its ID.
   *
   * Makes a POST request to the `ORDER_DETAILS` API endpoint with the provided `orderId`,
   * retrieves the order data, and updates local state variables:
   * - `orderNumber` (order identifier),
   * - `orderDetails` (full order data),
   * - `estCompletionDate` (parsed estimated completion date).
   *
   * Displays a toast message if the request fails.
   *
   * @async
   * @function fetchServiceTaskDetails
   * @returns {Promise<void>} A Promise that resolves when the data is successfully fetched and state is updated.
   * @throws Will throw an error if the API call fails.
   */
  const fetchServiceTaskDetails = async (): Promise<void> => {
    try {
      const response = await Axios.post(
        ApiEndpoints.SERVICE_PROCESS_TASK_DETAILS,
        {
          orderSpeciesId: orderSpeciesId,
        },
      );
      const orderDetails = response.data.data;
      setOrderNumber(orderDetails[0].orderNumber);
      setOrderDetails(orderDetails[0]);
      setSpeciesName(orderDetails[0].speciesTag);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Opens the service notes modal in "view" mode for a given service.
   *
   * @param {ServiceTaskDetails} service - The service object whose notes will be displayed.
   *
   * This function:
   * 1. Sets the active service for notes to the provided service object.
   * 2. Opens the service notes modal.
   * 3. Switches the modal mode to "view" to prevent editing.
   *
   */
  const handleViewServiceNotes = (service: ServiceTaskDetails): void => {
    setActiveServiceForNotes(service);
    setShowServiceNotesModal(true);
    setServiceNotesModalMode('view');
  };

  /**
   * Prepares the UI and state for editing notes of a specific service.
   *
   * @param {ServiceTaskDetails} service - The service object whose notes will be edited.
   *
   * This function:
   * 1. Sets the edit mode and editing service IDs to the given service's ID.
   * 2. Closes the service notes modal (if open).
   * 3. Fetches the latest service task details to ensure data is up-to-date before editing.
   *
   */
  const handleEditNotes = (service: ServiceTaskDetails): void => {
    setEditModeId(service.orderServiceId);
    setEditingServiceId(service.orderServiceId);
    setShowServiceNotesModal(false); // Ensure modal is closed
    fetchServiceTaskDetails();
  };

  /**
   * Increases the final measurement value for a specific service by 0.5.
   *
   * If the current measurement doesn't exist, it defaults to 1 before incrementing.
   * Updates the `finalMeasurements` state for the given `serviceId`.
   *
   * @param {number} serviceId - The ID of the service whose measurement is being increased.
   */
  const handleIncrement = (serviceId: number): void => {
    setFinalMeasurements((prev) => ({
      ...prev,
      [serviceId]: (prev[serviceId] || 1) + 0.5,
    }));
  };
  /**
   * Decreases the final measurement value for a specific service by 0.5.
   *
   * Ensures the value does not go below 0. Updates the `finalMeasurements` state
   * for the specified `serviceId`.
   *
   * @param {number} serviceId - The ID of the service whose measurement is being decreased.
   */
  const handleDecrement = (serviceId: number): void => {
    setFinalMeasurements((prev) => {
      const current = prev[serviceId] || 0;
      return {
        ...prev,
        [serviceId]: current > 0 ? current - 0.5 : 0,
      };
    });
  };

  /**
   * Updates the final measurement value for a specific service.
   *
   * This function updates the local state object `finalMeasurements` by setting
   * the measurement value for the given `serviceId`.
   *
   * @param {number} serviceId - The ID of the service whose measurement is being updated.
   * @param {number} value - The new final measurement value to set.
   */
  const handleMeasurementChange = (serviceId: number, value: number): void => {
    setFinalMeasurements((prev) => ({
      ...prev,
      [serviceId]: value,
    }));
  };

  /**
   * Checks whether all services in the list have been completed.
   *
   * A service is considered completed if its `orderServiceStatus` is `2`.
   * If all services are completed, the function updates the current step
   * to `"completed"` by calling `setCurrentStep`.
   */
  const checkAllServicesCompleted = (): void => {
    const currentServiceDetails = orderDetails?.services.filter(
      (service) => service.orderServiceId === currentServiceId,
    );

    const allCompleted = currentServiceDetails?.every(
      (service) => service.orderServiceStatus === 2,
    );
    if (allCompleted) {
      setCurrentStep('completed');
    }
  };

  /**
   * Marks a service as finished by updating its status and final measurement.
   *
   * This function sends a POST request to update the service workflow status
   * to `2` (finished) along with the provided final measurement. Upon success,
   * it shows a success toast message and refreshes the service task details.
   * In case of an error, it shows an error toast and rethrows the error.
   *
   * @param {number} orderServiceId - The unique identifier of the service to update.
   * @param {number} finalMeasurement - The final measurement value recorded after service processing.
   *
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   *
   * @throws Will throw an error if the update request fails.
   */
  const handleCompleteProcessing = async (
    orderServiceId: number,
    finalMeasurement: number,
  ): Promise<void> => {
    try {
      await Axios.post(ApiEndpoints.SERVICE_PROCESS_WORKFLOW, {
        orderServiceId: orderServiceId,
        status: OrderProcessStatus.COMPLETED,
        finalMeasurement: finalMeasurement,
      });
      showToastMessage(
        responseTexts.SERVICE_WORKFLOW_FINISH_SUCCESS,
        'success',
      );
      checkAllServicesCompleted();
      fetchServiceTaskDetails();
      // Setting the editing mode null
      setEditModeId(null);
      setEditingServiceId(null);
    } catch (error) {
      showToastMessage(errorMessage.ERROR_UPDATING_SERVICE_WORKFLOW, 'error');
    }
  };

  /**
   * Configuration object for rendering breadcrumbs in the Service Processing view.
   *
   * @property {Array} steps - The list of breadcrumb steps, typically imported from `ServiceProcessingCrumbSteps`.
   * @property {string} activeItem - The key representing the currently active breadcrumb step.
   * @property {string | number} orderNumber - The order number associated with the current service process.
   * @property {string} speciesName - The name of the species related to the current order.
   * @property {OrderProcessingMasterData} type - The type of order processing being handled; here, it's service processing.
   * @property {string} processItem_name - The name of the currently active process item (displayed in breadcrumbs).
   */
  const BreadCrumbsProps = {
    steps: ServiceProcessingCrumbSteps,
    activeItem: 'processItem_name',
    orderNumber: orderNumber,
    speciesName: speciesName,
    type: OrderProcessingMasterData.SERVICE_PROCESSING,
    processItem_name: currentServiceName,
    stateProp: orderDetails,
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={4} />
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-700">
              <button
                type="button"
                onClick={() => window.history.go(-2)} // navigating to service processing details
                className="p-1"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                {speciesName}{' '}
                {orderDetails?.orderCheckinInches &&
                  orderDetails.orderCheckinFeet && (
                    <>
                      {''} ({orderDetails.orderCheckinFeet} {Length.FEET}{' '}
                      {orderDetails.orderCheckinInches} {Length.INCHES})
                    </>
                  )}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => setShowNotesModal(true)}
              className="text-primary p-2 font-bold"
            >
              View Notes
            </button>
          </div>
          {showNotesModal && (
            <OrderNotesModal
              speciesName={orderDetails?.speciesTag ?? ''}
              onClose={() => setShowNotesModal(false)}
              checkInNotes={
                orderDetails?.orderCheckinNotes != ''
                  ? (orderDetails?.orderCheckinNotes ?? '')
                  : errorMessage.NO_NOTES_FOUND
              }
              AdditionalNotes={
                orderDetails?.orderSpeciesNotes != ''
                  ? (orderDetails?.orderSpeciesNotes ?? '')
                  : errorMessage.NO_NOTES_FOUND
              }
            />
          )}
          <ServiceTaskProgress currentStep={currentStep} type="service" />
          {orderDetails?.services
            ?.filter((service) => service.orderServiceId === currentServiceId)
            .map((service) => (
              <div
                key={service.orderServiceId}
                className={`rounded-md border shadow-md mt-4 ${
                  // If currently in edit mode for this service, apply edit background
                  editModeId === service.orderServiceId
                    ? 'bg-[#FFF9E6]'
                    : // Else if service is in initial or processing state, apply processing background
                      service.orderServiceStatus ===
                          OrderProcessStatus.INITIAL ||
                        service.orderServiceStatus ===
                          OrderProcessStatus.INPROGRESS
                      ? 'bg-quaternary'
                      : // Else default background
                        'bg-[#FFFFFF]'
                }`}
              >
                <div>
                  <div className="p-3 rounded-md flex items-center gap-4">
                    <img
                      src={orderDetails.imageUrl}
                      alt={orderDetails.speciesTag}
                      className="w-20 h-20 object-cover rounded-md"
                    />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-lg">{service.serviceName}</p>
                        {/* Show Add Notes button only if status is 1 */}
                        <div className="flex gap-2">
                          {editingServiceId === service.orderServiceId ? (
                            <div className="flex justify-between gap-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setActiveServiceForNotes(service);
                                  setShowServiceNotesModal(true);
                                  setServiceNotesModalMode('edit');
                                }}
                                className="font-bold text-primary flex items-center gap-1"
                              >
                                <FontAwesomeIcon icon={faFilePen} />
                                {service.serviceNotes &&
                                service.serviceNotes.length > 0
                                  ? 'Edit Notes'
                                  : 'Add Notes'}
                              </button>
                              <span className="text-[#00000033] text-2xl">
                                |{' '}
                              </span>
                              <OrderStatusBadge
                                status={service.orderServiceStatus}
                                type="serviceStatus"
                              />
                            </div>
                          ) : (
                            <div>
                              {service.orderServiceStatus ===
                                OrderProcessStatus.INPROGRESS ||
                              service.orderServiceStatus ===
                                OrderProcessStatus.INITIAL ? (
                                <div className="flex justify-between gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setActiveServiceForNotes(service);
                                      setShowServiceNotesModal(true);
                                      setServiceNotesModalMode('edit');
                                    }}
                                    className="font-bold text-primary flex items-center gap-1"
                                  >
                                    <FontAwesomeIcon icon={faFilePen} />
                                    {service.serviceNotes &&
                                    service.serviceNotes.length > 0
                                      ? 'Edit Notes'
                                      : 'Add Notes'}
                                  </button>
                                  <span className="text-[#00000033] text-2xl">
                                    |{' '}
                                  </span>
                                  <OrderStatusBadge
                                    status={service.orderServiceStatus}
                                    type="serviceStatus"
                                  />
                                </div>
                              ) : (
                                <div className="flex justify-between gap-1">
                                  {/* When the order product status is more than 3 (completed), show view notes, Edit and Status badge */}
                                  {service.orderServiceStatus ==
                                    OrderProcessStatus.COMPLETED && (
                                    <>
                                      <button
                                        onClick={() =>
                                          handleViewServiceNotes(service)
                                        }
                                        type="button"
                                        className="font-bold text-primary flex items-center gap-1"
                                      >
                                        View Notes
                                      </button>
                                      <span className="text-[#00000033] text-2xl">
                                        |{' '}
                                      </span>
                                      <button
                                        onClick={() => handleEditNotes(service)}
                                        type="button"
                                        className="font-bold text-primary flex items-center gap-2"
                                      >
                                        <FontAwesomeIcon icon={faPen} />
                                        Edit
                                      </button>
                                      <span className="text-[#00000033] text-2xl">
                                        |{' '}
                                      </span>
                                      <OrderStatusBadge
                                        status={service.orderServiceStatus}
                                        type="serviceStatus"
                                      />
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 1 means order service is in progress, 2 means order service is completed */}
                      {service.serviceFlatFeeFlag === 1 ? (
                        // For Flat Fee, no measurements required as per the UI.
                        <div className="bg-[#F5F5F5] p-2 rounded-md text-sm inline-block">
                          Flat Fee
                        </div>
                      ) : [0, 1].includes(service.orderServiceStatus) ||
                        editingServiceId === service.orderServiceId ? (
                        // Show input block for non-flat fee services when processing or in edit mode
                        <div className="bg-[#FFFFFF] p-2 rounded-md flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                          <span>Measurements after processing</span>
                          <form id="measurement-form">
                            <div className="flex items-center">
                              <button
                                type="button"
                                onClick={() =>
                                  handleDecrement(service.orderServiceId)
                                }
                                className="w-12 h-8 flex items-center justify-center border rounded-l text-white text-lg bg-primary hover:bg-primary"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                value={
                                  finalMeasurements[service.orderServiceId] ??
                                  service.serviceProcessDetail
                                    ?.finalMeasurement ??
                                  0
                                }
                                onChange={(e) =>
                                  handleMeasurementChange(
                                    service.orderServiceId,
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="w-20 h-8 text-center border-t border-b focus:outline-primary"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  handleIncrement(service.orderServiceId)
                                }
                                className="w-12 h-8 flex items-center justify-center border rounded-r text-white text-lg bg-primary hover:bg-primary"
                              >
                                +
                              </button>
                              <span className="ml-2">
                                {service.serviceUnit}
                              </span>
                            </div>
                          </form>
                        </div>
                      ) : (
                        // Otherwise, show the final measurement or fallback message
                        <div className="bg-[#F5F5F5] w-full p-2 rounded-md text-sm inline-block">
                          {service.serviceProcessDetail?.finalMeasurement
                            ? `Final measurements: ${service.serviceProcessDetail.finalMeasurement} ${service.serviceUnit}`
                            : 'Final Price to be calculated after processing'}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Start Date and End Date */}
                  {service.serviceProcessDetail &&
                    service.serviceProcessDetail.serviceStartDate && (
                      <div className="px-3 py-1 flex justify-between">
                        {service.serviceProcessDetail.serviceStartDate && (
                          <span className="text-gray-600 font-semibold">
                            Start date:{' '}
                            {new Date(
                              service.serviceProcessDetail.serviceStartDate,
                            ).toLocaleDateString('en-US')}
                          </span>
                        )}
                        {service.serviceProcessDetail.serviceEndDate && (
                          <span className="text-gray-600 font-semibold">
                            End date:{' '}
                            {new Date(
                              service.serviceProcessDetail.serviceEndDate,
                            ).toLocaleDateString('en-US')}
                          </span>
                        )}
                      </div>
                    )}
                  <div className="p-3">
                    {/* If status is in-progress or initial state, display Complete Processing button */}
                    {service.orderServiceStatus ===
                      OrderProcessStatus.INPROGRESS ||
                    service.orderServiceStatus ===
                      OrderProcessStatus.INITIAL ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleCompleteProcessing(
                            service.orderServiceId,
                            finalMeasurements[service.orderServiceId] ?? 0,
                          )
                        }
                        className="w-full mb-3 px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                      >
                        Complete Processing
                      </button>
                    ) : // Else if the service is getting edited, display save changes and cancel
                    editingServiceId === service.orderServiceId ? (
                      // Cancel and Save Changes
                      <div className="flex justify-between gap-4 mt-3">
                        <button
                          onClick={() => {
                            setEditModeId(null);
                            setEditingServiceId(null);
                          }}
                          type="button"
                          className="w-[50%] px-5 py-2 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          onClick={() =>
                            handleCompleteProcessing(
                              service.orderServiceId,
                              finalMeasurements[service.orderServiceId] ?? 0,
                            )
                          }
                          className="w-[50%] px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                        >
                          <span>Save Changes</span>
                        </button>
                      </div>
                    ) : (
                      // Display service is completed when the status is not in-progress(except 2)
                      <span className="inline-block w-full border bg-[#CCEECC] p-2 text-[#2D5B4A] border-[#2D5B4A] text-center font-semibold rounded-md">
                        Service Completed
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="ml-2 text-primary"
                        />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          {/* Service Notes Modal */}
          <ServiceNotesModal
            isOpen={showServiceNotesModal}
            onClose={() => {
              setShowServiceNotesModal(false);
              setActiveServiceForNotes(null);
              setEditingServiceId(null);
              fetchServiceTaskDetails();
            }}
            serviceNotes={activeServiceForNotes?.serviceNotes || []}
            orderId={orderDetails?.orderId ?? 0}
            orderSpeciesId={orderDetails?.orderSpeciesId ?? 0}
            orderServiceId={activeServiceForNotes?.orderServiceId ?? 0}
            showToastMessage={showToastMessage}
            mode={serviceNotesModalMode}
          />
        </div>
      </div>
    </div>
  );
};
